import React from 'react';
import { cn } from '@/utils/cn';

import { Link } from 'react-router-dom';
import { openExternalUrl } from '@/utils/openExternalUrl';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'default' | 'destructive';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'icon';
  fullWidth?: boolean;
  loading?: boolean;
  loadingType?: 'spinner' | 'dots' | 'pulse';
  children?: React.ReactNode;
  href?: string;
  // Optional manual classes to customize the outline variant
  borderClass?: string; // e.g. 'border-primary-500' or 'border-primary-500'
  textClass?: string; // e.g. 'text-primary-500'
  hoverBorderClass?: string; // e.g. 'hover:border-primary-600'
  hoverBgClass?: string; // e.g. 'hover:bg-primary-50'
  roundedClass?: string; // e.g. 'rounded-md' or 'rounded-full'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      loadingType = 'dots',
      href,
      className,
      children,
      disabled,
      borderClass,
      textClass,
      hoverBorderClass,
      hoverBgClass,
      roundedClass,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'relative inline-flex items-center justify-center font-medium ' +
      (roundedClass ?? 'rounded-full') +
      ' outline-none focus:outline-none lg:focus-visible:ring-2 lg:focus-visible:ring-primary-500 lg:focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer overflow-hidden text-base py-[10px] px-3 active:scale-[0.98]';

    const variants = {
      default: 'bg-primary-500 text-white hover:bg-primary-600',
      primary: 'bg-primary-500 text-white hover:bg-primary-600',
      secondary:
        'bg-gray-100 border-2 border-gray-200 text-gray-700 hover:bg-gray-200 hover:border-gray-300',
      // ghost kept as a simple option
      ghost: 'text-gray-700 hover:bg-gray-100 hover:text-gray-800',
      destructive: 'bg-red-500 text-white hover:bg-red-600',
    };

    // Build dynamic outline classes. If caller provides border/text/hover classes,
    // use them; otherwise fall back to defaults that match previous behavior.
    const outlineVariant = [
      'border-2',
      borderClass ?? 'border-primary-500',
      textClass ?? 'text-primary-500',
      'bg-transparent',
      hoverBgClass ?? 'hover:bg-primary-50',
      hoverBorderClass ?? 'hover:border-primary-600',
    ]
      .filter(Boolean)
      .join(' ');

    const variantClass =
      variant === 'outline' ? outlineVariant : variants[variant as keyof typeof variants] || variants.primary;

    const sizes = {
      sm: 'px-2 py-1 text-sm',
      md: 'px-3 py-[10px] text-base',
      lg: 'px-4 py-3 text-lg',
      xl: 'px-6 py-4 text-xl',
      icon: 'w-8 h-8 p-0 text-xs',
    } as const;

    const widthClass = fullWidth ? 'w-full' : '';

    // Loading animations
    const renderLoadingAnimation = () => {
      if (!loading) return null;

      switch (loadingType) {
        case 'spinner':
          return (
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          );

        case 'dots':
          return (
            <div className="absolute inset-0 flex items-center justify-center space-x-1">
              <div className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="w-2 h-2 bg-current rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="w-2 h-2 bg-current rounded-full animate-bounce"></div>
            </div>
          );

        case 'pulse':
          return (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-4 h-4 bg-current rounded-full animate-pulse"></div>
            </div>
          );

        default:
          return null;
      }
    };

    // If href is provided, render as <a> or <Link>
    if (typeof href === 'string' && href.length > 0) {
      const isExternal = /^https?:\/\//.test(href);
      const content = (
        <span className={cn('flex items-center justify-center', loading && 'opacity-0')}>
          {children}
        </span>
      );
      if (isExternal) {
        return (
          <a
            className={cn(
              baseClasses,
              variantClass,
              sizes[size],
              widthClass,
              loading && 'pointer-events-none',
              className
            )}
            href={href}
            onClick={(event) => {
              event.preventDefault();
              void openExternalUrl(href);
            }}
          >
            {content}
          </a>
        );
      } else {
        return (
          <Link
            className={cn(
              baseClasses,
              variantClass,
              sizes[size],
              widthClass,
              loading && 'pointer-events-none',
              className
            )}
            to={href}
          >
            {content}
          </Link>
        );
      }
    }

    // Default: render as <button>
    return (
      <button
        ref={ref}
        className={cn(
          baseClasses,
          variantClass,
          sizes[size],
          widthClass,
          loading && 'pointer-events-none',
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {/* Loading overlay */}

        {renderLoadingAnimation()}

        {/* Content */}
        <span className={cn('flex items-center justify-center', loading && 'opacity-0')}>
          {children}
        </span>
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, type ButtonProps };
export default Button;
